import "react-native-gesture-handler";
import React, { ReactNode } from "react";
import { Provider } from "react-redux";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GluestackUIProvider } from "@gluestack-ui/themed";
import { config } from "@gluestack-ui/config";
import { PersistGate } from "redux-persist/integration/react";
import { persistor, store } from "@redux/store";
import ThemeProvider from "@navigations/utils/ThemeProvider";
import { SafeAreaProviderCompat } from "@react-navigation/elements";
import { ScrollContextProvider } from "@navigations/utils/ScrollContext";
import { KeyboardProvider } from "react-native-keyboard-controller";
import { PortalProvider } from "@gorhom/portal";
import { useReactQueryDevTools } from "@dev-plugins/react-query";
import { HelmetProvider } from "react-helmet-async";
import { AnimationProvider } from "../../contexts/AnimationContext";
import { FontProvider } from "../../contexts/FontContext";
import { Platform } from "react-native";
import { useInjectMetaTagsAndDatadog } from "../../hooks/useInjectScript";
import { MenuProvider } from "react-native-popup-menu";
import { FoldableWrapper } from "@navigations/utils/FoldableWrapper";
import SurveySegmentClientWrapper from "./SurveySegmentClientWrapper";
import { InitialUrlProvider } from "../../contexts/InitialUrlContext";
import { useMemoryWarningHandler } from "../../hooks/useMemoryWarningHandler";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      cacheTime: 60_000,
      // `navigator.onLine` is unreliable under react-native-web and can report
      // offline while the network is fine. The default "online" mode would then
      // park every query at fetchStatus "paused" — no request, no error — which
      // strands bootstrap on the splash loader forever. Always attempt the
      // request and let real failures surface through the existing error paths.
      networkMode: "always",
    },
    mutations: {
      cacheTime: 0,
      networkMode: "always",
    },
  },
});

const AppProviders = ({ children }: { children: ReactNode }) => {
  // @ts-ignore
  if (__DEV__) useReactQueryDevTools(queryClient);

  if (Platform.OS === "web") {
    useInjectMetaTagsAndDatadog();
  }

  useMemoryWarningHandler();

  const helmetContext = {};

  return (
    <PortalProvider>
      <GestureHandlerRootView style={{ flex: 1 }}>
        <PersistGate loading={null} persistor={persistor}>
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <FontProvider>
                <KeyboardProvider>
                  <ThemeProvider>
                    <GluestackUIProvider colorMode={"light"} config={config}>
                      <SafeAreaProviderCompat>
                        <HelmetProvider context={helmetContext}>
                          <AnimationProvider>
                            <MenuProvider>
                              <FoldableWrapper>
                                <ScrollContextProvider>
                                  <InitialUrlProvider>
                                    <SurveySegmentClientWrapper>
                                      {children}
                                    </SurveySegmentClientWrapper>
                                  </InitialUrlProvider>
                                </ScrollContextProvider>
                              </FoldableWrapper>
                            </MenuProvider>
                          </AnimationProvider>
                        </HelmetProvider>
                      </SafeAreaProviderCompat>
                    </GluestackUIProvider>
                  </ThemeProvider>
                </KeyboardProvider>
              </FontProvider>
            </QueryClientProvider>
          </Provider>
        </PersistGate>
      </GestureHandlerRootView>
    </PortalProvider>
  );
};

export default AppProviders;
